<template>
    <div>
        <b-row>
            <b-col cols="9">
                <app-sub-title>Descripción de la solución</app-sub-title>
            </b-col>
        </b-row>
        <b-col cols="3" v-if="task.finish_date && (task.user_id === currentUser.id)">
            <b-button 
                size="sm" 
                variant="primary" 
                @click="toggleEditView"
                block
            >
                <i class="fas" :class="isEditingView ? 'fa-times' : 'fa-edit'"></i>
                {{ isEditingView ? 'Cancelar edición' : 'Editar solución' }}
            </b-button>
        </b-col>

        <!-- Vista de solo lectura -->
        <template v-if="!isEditingView && task.finish_date">
            <b-card class="m-4">
                <b-row class="mt-4" v-if="task.resolution">
                    <b-col>
                        <div v-html="task.resolution"></div>
                    </b-col>
                </b-row>
                <b-row class="mt-4" v-else>
                    <b-col>
                        <div>No hay descripción de la solución.</div>
                    </b-col>
                </b-row>
            <!-- Archivos existentes en modo lectura -->
            <b-row v-if="task.files && task.files.length" class="mt-4">
                <b-col>
                    <h6>Archivos adjuntos:</h6>
                    <ul class="list-unstyled">
                        <li v-for="file in task.files" :key="file.id">
                            <a :href="file.url" target="_blank">
                                <i class="fas fa-file"></i> {{ file.name }}
                            </a>
                        </li>
                    </ul>
                </b-col>
            </b-row>
            </b-card>
        </template>

        <!-- Vista de edición -->
        <template v-else>
            <b-row class="mt-4">
                <b-col>
                    <vue-editor v-model="resolution"></vue-editor>
                </b-col>
            </b-row>
            <b-row class="mt-4">
                <b-col cols="12" class="mt-2">
                    <app-form-file
                        multiple
                        v-model="files"
                        id="files"
                        v-on:change="handleFileChange"
                        placeholder="Selecciona los archivos"
                        drop-placeholder="Arrastre el archivo acá..."
                        accept=".xlsx, .xls, image/*, .doc, .docx, .ppt, .pptx, .txt, .pdf"
                        :file-name-formatter="filesNameFormatter"
                    ></app-form-file>
                </b-col>
                <!-- Archivos previamente cargados -->
                <b-col cols="12" v-if="!editingResolution">
                    <b-alert :variant="validFilesSize ? 'warning' : 'danger'" class="mt-2" :show="!!oldFiles.length">
                        <ol>
                            <li v-for="(file, index) in oldFiles" :key="index" class="d-flex justify-content-between">
                                {{ file.name }} ({{ (file.size / 1000 / 1000) | decimal(2) }}MB)
                                <b-checkbox v-model="filesToDelete" :value="file.id"><i class="fas fa-trash"></i></b-checkbox>
                            </li>
                        </ol>
                    </b-alert>
                </b-col>
            </b-row>
            <b-row class="m-3">
                <b-col cols="12">
                    <b-alert :variant="validFilesSize ? 'info' : 'danger'" class="mt-2" :show="!!selectedFiles.length">
                        <ol>
                            <li v-for="(file, index) in selectedFiles" :key="index" class="mt-2">
                                {{ file.name }} ({{ (file.size / 1000 / 1000) | decimal(2) }}MB)
                                <b-btn variant="danger" size="sm" title="Eliminar" @click="deleteFile(index)">
                                    <i class="fas fa-trash"></i>
                                </b-btn>
                            </li>
                        </ol>
                    </b-alert>
                </b-col>
            </b-row>
        </template>
    </div>
</template>

<script>
import { VueEditor } from 'vue2-editor'

export default {
    components: {
        VueEditor,
    },
    props: {
        task: {
            type: Object,
            required: true,
        },
        editingResolution: {
            type: Boolean,
            default: false,
        }
    },
    data: () => ({
        isEditingView: false,
        resolution: '',
        files: [],
        oldFiles: [],
        filesToDelete: [],
        selectedFiles: [],
    }),
    watch: {
        task: {
            handler: function() {
                this.initializeData()
            },
    }
    },
    computed: {
        filesSize() {
            const filesSize = this.files.reduce((carry, file) => carry + file.size, 0)
            return filesSize / 1000 / 1000
        },
        validFilesSize() {
            return this.filesSize < 9
        },
    },
 
    methods: {
        toggleEditView() {
            this.isEditingView = !this.isEditingView;
        },
        initializeData() {
            this.resolution = this.task.resolution || ''
            this.oldFiles = this.task.files || []
        },
        handleFileChange(event) {
            for (let i = 0; i < event.target.files.length; i++) {
                this.selectedFiles.push(event.target.files[i]);
            }
        },
        deleteFile(idx) {
            this.selectedFiles.splice(idx, 1)
        },
        cancelFilesSelected() {
            this.files = []
            toastr.success('Archivos cancelados')
        },
        filesNameFormatter(files) {
            return `${files.length} archivos seleccionados`
        },
        async submit() {
             // Si estamos en modo visualización (no edición) y no es una tarea nueva
            if(!this.isEditingView && !this.editingResolution && this.task.finish_date){
                return this.$emit('close-resolution-task-window')
            }
            if (!this.validFilesSize) return toastr.error('Los archivos no deben pesar más de 9 MB')
            try {
                this.$emit('busy', true)
                const formData = new FormData();
                formData.append('resolution', this.resolution)
                formData.append('filesToDelete', this.filesToDelete)
                this.selectedFiles.forEach((file, i) => formData.append(`file[${i}]`, file));
                
                const { data } = await this.$repo.post(`administrative-tasks/${this.task.id}/finish-task`, formData)
                if(data){
                    toastr.success('La solución fue actualizada satisfactoriamente')
                }
                this.isEditingView = false
            } catch (error) {
                toastr.error('Ocurrió un error actualizando la solución. Por favor intente de nuevo más tarde')
            } finally {
                this.$emit('busy', false)
            }
            this.$emit('finished-task', this.task)
        },
    },

    mounted() {
        this.initializeData()
    },
}
</script>
